import { scaleLinear } from 'd3-scale';

import { Vertical, Horizontal, Th, Td, Total, Empty, Formatter, formatDefault, formatDate } from './utils'

export default class PivotData {
  constructor({ data, cols, rows, metric, showHeatmap, heatmapColor, hoverCell, onHoverCell }) {
    this.data = data
    this.colAttrs = cols
    this.rowAttrs = rows
    this.metric = metric
    this.showHeatmap = showHeatmap
    this.heatmapColor = heatmapColor
    this.hoverCell = hoverCell
    this.onHoverCell = onHoverCell
    this.highlightColor = 'bg-gray-300 dark:bg-gray-1000'

    this.init()
  }

  highlightCol(colIndex) {
    return this.hoverCell != null && this.hoverCell.col === colIndex ? this.highlightColor : ''
  }

  highlightRow(rowIndex) {
    return this.hoverCell != null && this.hoverCell.row === rowIndex ? this.highlightColor : ''
  }

  highlightCross(rowIdx, colIdx) {
    if (this.hoverCell == null) return ''
    const { row, col } = this.hoverCell
    if (rowIdx === row || colIdx === col) return this.highlightColor
    return ''
  }

  hoverHandlers(row, col) {
    if (!this.onHoverCell) return {}
    return { onMouseEnter: () => this.onHoverCell({ row, col }) }
  }

  init() {
    this.uniqColKeys = {}
    this.colKeys = []
    this.uniqRowKeys = {}
    this.rowKeys = []
    this.colMap = {}
    this.rowMap = {}
    this.valueMap = {}
    this.colTotalMap = {}
    this.colKey = ""
    this.maxValue = 0
  }

  getHeatmapRange() {
    const { showHeatmap, heatmapColor } = this

    if (showHeatmap) {
      const colorRange = COLORS[heatmapColor]
      const setColor = scaleLinear().domain([0, this.maxValue]).range(colorRange)
      return level => ({ backgroundColor: setColor(level || 0) })
    }

    return () => ({})
  }

  getValue(row, col) {
    const x = this.valueMap[row.join('.')]
    return x ? x[col.join('.')] || null : null
  }

  getTotal(keys) {
    const { rowKey, colKey } = keys
    if (rowKey) return Object.values(this.valueMap[rowKey.join('.')] || []).reduce((total, value) => total+value, 0) || 0
    if (colKey) return this.colTotalMap[colKey.join('.')] || 0
    return Object.values(this.colTotalMap).reduce((total, value) => total+value, 0) || 0
  }

  getSize(arr, i, j) {
    let x;
    if (i !== 0) {
      let asc; let end;
      let noDraw = true;
      for (
        x = 0, end = j, asc = end >= 0;
        asc ? x <= end : x >= end;
        asc ? x++ : x--
      ) {
        if (arr[i - 1][x] !== arr[i][x]) {
          noDraw = false;
        }
      }
      if (noDraw) {
        return -1;
      }
    }
    let len = 0;
    while (i + len < arr.length) {
      let asc1; let end1;
      let stop = false;
      for (
        x = 0, end1 = j, asc1 = end1 >= 0;
        asc1 ? x <= end1 : x >= end1;
        asc1 ? x++ : x--
      ) {
        if (arr[i][x] !== arr[i + len][x]) {
          stop = true;
        }
      }
      if (stop) {
        break;
      }
      len++;
    }
    return len;
  }

  getScroll() {
    const { innerWidth, innerHeight } = window
    const widthColSize = 160
    const heightRowSize = 27
    const scrollX = innerWidth <= Object.keys(this.uniqColKeys || []).length*widthColSize + 230
    const scrollY = innerHeight <= Object.keys(this.uniqRowKeys || []).length*heightRowSize + 240
    return { scrollX, scrollY }
  }

  formatValue(currentChildren) {
    const { formattedName, key, name, value } = currentChildren
    if (formattedName) return formattedName
    if (key && ['hour', 'day', 'month', 'week'].includes(key)) return formatDate(key, value)
    if (name && value) return formatDefault(name, value)
    if (name) return name
    return value
  }

  parseData(currentChildren, keys = '') {
    const { key, value, children } = currentChildren

    keys = key === 'root' ? "" : `${keys ? `${keys}.` : ""}${value || 'N/A'}`

    if (this.colAttrs[this.colAttrs.length-1] === key) {
      if (!this.uniqColKeys[keys]) {
        this.colKeys.push(keys.split('.'))
        this.uniqColKeys[keys] = true
      }
      this.colTotalMap[keys] = currentChildren[this.metric]
      this.colKey = keys
      keys = ""
    }

    if (this.rowAttrs[this.rowAttrs.length-1] === key) {
      if (!this.uniqRowKeys[keys]) {
        this.rowKeys.push(keys.split('.'))
        this.uniqRowKeys[keys] = true
      }
      let _value = currentChildren[this.metric]
      this.valueMap[keys] = this.valueMap[keys] || {}
      this.valueMap[keys][this.colKey] = _value
      _value = Number(_value)
      if (_value && _value > this.maxValue) {
        this.maxValue = _value
      }
    }

    if (this.colAttrs.includes(key)) {
      this.colMap[value] = this.formatValue(currentChildren)
    }

    if (this.rowAttrs.includes(key)) {
      this.rowMap[value] = this.formatValue(currentChildren)
    }

    if (children && children.length) {
      for (const child of children) {
        this.parseData(child, keys)
      }
    }
  }

  buildCols() {
    const Cols = []
    const cols = this.colAttrs.join(' / ')

    Cols.push([
      <Horizontal key={2} colSpan={this.colKeys.length} rowSpan={1}>{cols}</Horizontal>,
      <Total th key={3} colSpan={1} rowSpan={this.colAttrs.length+1} className={this.highlightCol(this.colKeys.length)}>Totals</Total>,
    ])

    this.colAttrs.forEach((c, j) => {
      const cells = []
      this.colKeys.forEach((colKey, i) => {
        const x = this.getSize(this.colKeys, i, j);
        if (x !== -1) cells.push(<Th colSpan={x} rowSpan={1} colorWeigth={j} id={`col-${i}-${j}`} className={this.highlightCol(i)}>{this.colMap[colKey[j]]}</Th>)
      })
      Cols.push(cells)
    })

    return Cols
  }

  buildRows() {
    const Rows = []
    const rows = this.rowAttrs.join(' / ')

    this.rowKeys.forEach((rowKey, i) => {
      const cells = []
      if (i === 0) cells.push(<Vertical colSpan={1} rowSpan={this.rowKeys.length} height={this.rowKeys.length}>{rows}</Vertical>)
      rowKey.forEach((value, j) => {
        const x = this.getSize(this.rowKeys, i, j);
        if (x !== -1) cells.push(<Th row colSpan={1} rowSpan={x} colorWeigth={j} id={`row-${i}-${j}`} className={this.highlightRow(i)}>{this.rowMap[value]}</Th>)
      })
      Rows.push(cells)
    })
    Rows.push([<Total th key={1} colSpan={this.rowAttrs.length+1} rowSpan={1} className={this.highlightRow(this.rowKeys.length)}>Totals</Total>])

    return Rows
  }

  buildBody() {
    const Body = []
    const colorFormat = this.getHeatmapRange()

    this.rowKeys.forEach((rowKey, rowIdx) => {
      const cells = []
      this.colKeys.forEach((colKey, colIdx) => {
        const value = this.getValue(rowKey, colKey)
        cells.push(<Td colSpan={1} rowSpan={1} colorStyle={colorFormat(value)} className={this.highlightCross(rowIdx, colIdx)} {...this.hoverHandlers(rowIdx, colIdx)}><Formatter>{value}</Formatter></Td>)
      })
      cells.push(<Total colSpan={1} rowSpan={1} className={this.highlightCross(rowIdx, this.colKeys.length)} {...this.hoverHandlers(rowIdx, this.colKeys.length)}><Formatter>{this.getTotal({ rowKey })}</Formatter></Total>)
      Body.push(cells)
    })

    const totals = []
    this.colKeys.forEach((colKey, colIdx) => {
      totals.push(<Total colSpan={1} rowSpan={1} className={this.highlightCross(this.rowKeys.length, colIdx)} {...this.hoverHandlers(this.rowKeys.length, colIdx)}><Formatter>{this.getTotal({ colKey })}</Formatter></Total>)
    })
    totals.push(<Total colSpan={1} rowSpan={1} className={this.highlightCross(this.rowKeys.length, this.colKeys.length)} {...this.hoverHandlers(this.rowKeys.length, this.colKeys.length)}><Formatter>{this.getTotal({})}</Formatter></Total>)
    Body.push(totals)

    return Body
  }

  buildAll() {
    const Table = []
    const tHead = []
    const tBody = []
    const rows = this.rowAttrs.join(' / ')
    const cols = this.colAttrs.join(' / ')
    const colorFormat = this.getHeatmapRange()

    tHead.push([
      <Empty key={1} colSpan={this.rowAttrs.length+1} rowSpan={1} />,
      <Horizontal key={2} colSpan={this.colKeys.length} rowSpan={1}>{cols}</Horizontal>,
      <Total th key={3} colSpan={1} rowSpan={this.colAttrs.length+1} className={this.highlightCol(this.colKeys.length)}>Totals</Total>,
    ])

    this.colAttrs.forEach((c, j) => {
      const cells = []
      cells.push(<Empty key={1} colSpan={this.rowAttrs.length+1} rowSpan={1} />)
      this.colKeys.forEach((colKey, i) => {
        const x = this.getSize(this.colKeys, i, j);
        if (x !== -1) cells.push(<Th colSpan={x} rowSpan={1} colorWeigth={j} id={`col-${i}-${j}`} className={this.highlightCol(i)}>{this.colMap[colKey[j]]}</Th>)
      })
      tHead.push(cells)
    })

    Table.push(tHead)

    this.rowKeys.forEach((rowKey, i) => {
      const cells = []
      if (i === 0) cells.push(<Vertical colSpan={1} rowSpan={this.rowKeys.length}>{rows}</Vertical>)
      rowKey.forEach((value, j) => {
        const x = this.getSize(this.rowKeys, i, j);
        if (x !== -1) cells.push(<Th colSpan={1} rowSpan={x} colorWeigth={j} id={`row-${i}-${j}`} className={this.highlightRow(i)}>{this.rowMap[value]}</Th>)
      })
      this.colKeys.forEach((colKey, colIdx) => {
        const value = this.getValue(rowKey, colKey)
        cells.push(<Td colSpan={1} rowSpan={1} colorStyle={colorFormat(value)} className={this.highlightCross(i, colIdx)} {...this.hoverHandlers(i, colIdx)}><Formatter>{value}</Formatter></Td>)
      })
      cells.push(<Total colSpan={1} rowSpan={1} className={this.highlightCross(i, this.colKeys.length)} {...this.hoverHandlers(i, this.colKeys.length)}><Formatter>{this.getTotal({ rowKey })}</Formatter></Total>)
      tBody.push(cells)
    })

    const totals = [<Total key={1} colSpan={this.rowAttrs.length+1} rowSpan={1} className={this.highlightRow(this.rowKeys.length)}>Totals</Total>]
    this.colKeys.forEach((colKey, colIdx) => {
      totals.push(<Total colSpan={1} rowSpan={1} className={this.highlightCross(this.rowKeys.length, colIdx)} {...this.hoverHandlers(this.rowKeys.length, colIdx)}><Formatter>{this.getTotal({ colKey })}</Formatter></Total>)
    })
    totals.push(<Total colSpan={1} rowSpan={1} className={this.highlightCross(this.rowKeys.length, this.colKeys.length)} {...this.hoverHandlers(this.rowKeys.length, this.colKeys.length)}><Formatter>{this.getTotal({})}</Formatter></Total>)
    tBody.push(totals)
    Table.push(tBody)

    return Table
  }

  buildTable() {
    if (!Object.keys(this.valueMap || {}).length) {
      return {}
    }

    const { scrollX, scrollY } = this.getScroll()
    if (scrollX && !scrollY) {
      const Cols = this.buildCols()
      const Rows = this.buildRows()
      const Body = this.buildBody()
      return { sticky: 'th-rows', Cols, Rows, Body }
    }

    if (!scrollX === scrollY) {
      return { sticky: 'th-cols', Table: this.buildAll() }
    }
    return { Table: this.buildAll(), scroll: scrollX && scrollY }
  }

  handle() {
    this.parseData(this.data)
    return this.buildTable()
  }
}

const COLORS = {
  heatmap: ['rgb(253, 235, 124, 0.05)', 'rgb(255, 85, 111, 0.7)'],
  blue: ['rgb(120, 207, 231, 0.05)', 'rgb(120, 207, 231, 0.7)'],
}
